import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { EntitiesService } from '../entities/entities.service';
import { Entity } from '../entities/entities.model';

/**
 * Service to handle entity-related functionality for tasks
 * Manages entity dialogs, validation, and integration with task forms
 */
@Injectable({
  providedIn: 'root'
})
export class TaskEntityIntegrationService {
  // Entity dialog state
  entityDialog: boolean = false;
  entityToEdit: any = null;
  updateEntityDialog: boolean = false;
  
  // Entity data
  currentTaskEntity: any = null;
  entitiesList: any[] = [];
  isLoadingEntity: boolean = false;

  constructor(
    private entitiesService: EntitiesService,
    private messageService: MessageService
  ) {}

  /**
   * Check if a task is entity-related based on form value
   */
  isEntityRelatedTask(taskForm: UntypedFormGroup): boolean {
    const entityRelated = taskForm?.get('entityRelated')?.value;
    return entityRelated && entityRelated.trim().toUpperCase() === 'YES';
  }

  /**
   * Check if entity information is complete (all 3 TK-editable fields filled)
   */
  isEntityInfoComplete(): boolean {
    if (!this.currentTaskEntity) {
      return false;
    }
    
    const entity = this.currentTaskEntity;
    
    // Check if the 3 TK-editable fields are filled
    const formationDateFilled = !!entity.formationDate;
    const authorizationDateFilled = !!entity.authorizationDate;
    const jurisdictionIdFilled = !!entity.jurisdictionIdNumber;
    
    // If any of the 3 required fields are empty, entity info is NOT complete
    if (!formationDateFilled || !authorizationDateFilled || !jurisdictionIdFilled) {
      return false;
    }
    
    // All 3 fields are filled, entity info is complete
    return true;
  }

  /**
   * Open entity dialog for creating new entity
   */
  openEntityDialog(): void {
    this.entityToEdit = null;
    this.entityDialog = true;
  }

  /**
   * Close entity dialog
   */
  closeEntityDialog(): void {
    this.entityDialog = false;
    this.entityToEdit = null;
  }

  /**
   * Open update/show entity dialog
   */
  openUpdateEntityDialog(taskId: number): void {
    if (taskId) {
      this.loadEntityByTaskIdAndOpenDialog(taskId);
    } else {
      this.updateEntityDialog = true;
    }
  }

  /**
   * Close update entity dialog
   */
  closeUpdateEntityDialog(): void {
    this.updateEntityDialog = false;
  }

  /**
   * Load entity by task ID and open dialog
   */
  loadEntityByTaskIdAndOpenDialog(taskId: number): void {
    this.isLoadingEntity = true;
    
    this.entitiesService.getEntityByTaskId(taskId).subscribe({
      next: (response: any) => {
        this.isLoadingEntity = false;
        if (response.data) {
          this.currentTaskEntity = response.data;
          this.entityToEdit = { ...response.data };
          this.updateEntityDialog = true;
        } else {
          // Handle 204 NO_CONTENT case - no entity found for task
          this.messageService?.add({
            severity: 'info',
            summary: 'No Entity', 
            detail: 'No entity is associated with this task'
          });
          this.entityToEdit = null;
          this.currentTaskEntity = null;
          this.updateEntityDialog = true;
        }
      },
      error: (error: any) => {
        this.isLoadingEntity = false;
        console.error('Error loading entity by task ID:', error);
        this.messageService?.add({
          severity: 'error',
          summary: 'Error', 
          detail: 'Failed to load entity information for this task'
        });
        this.entityToEdit = null;
        this.currentTaskEntity = null;
      }
    });
  }

  /**
   * Load entity by task ID (without opening dialog)
   * Returns a callback for when entity is loaded
   */
  loadEntityByTaskId(taskId: number, onLoadComplete?: (entity: any) => void): void {
    this.isLoadingEntity = true;
    this.entitiesService.getEntityByTaskId(taskId).subscribe({
      next: (response: any) => {
        this.isLoadingEntity = false;
        if (response.data) {
          this.currentTaskEntity = response.data;
          this.entityToEdit = { ...response.data };
          
          // Call callback if provided
          if (onLoadComplete) {
            onLoadComplete(response.data);
          }
        } else {
          this.messageService?.add({
            severity: 'info',
            summary: 'No Entity', 
            detail: 'No entity is associated with this task'
          });
          this.entityToEdit = null;
          this.currentTaskEntity = null;
          
          if (onLoadComplete) {
            onLoadComplete(null);
          }
        }
      },
      error: (error: any) => {
        this.isLoadingEntity = false;
        console.error('Error loading entity by task ID:', error);
        this.messageService?.add({
          severity: 'error',
          summary: 'Error', 
          detail: 'Failed to load entity information for this task'
        });
        this.entityToEdit = null;
        this.currentTaskEntity = null;
        
        if (onLoadComplete) {
          onLoadComplete(null);
        }
      }
    });
  }

  /**
   * Load entity for editing by entity ID
   */
  loadEntityForEdit(entityId: number): void {
    if (entityId === null || entityId === undefined || (entityId as any) === '') {
      return;
    }
    this.entitiesService.getEntityById(entityId).subscribe({
      next: (response: any) => {
        this.currentTaskEntity = response.data;
        this.entityToEdit = { ...response.data };
        this.loadEntitiesForDropdown();
      },
      error: (error: any) => {
        console.error('Error loading entity for edit:', error);
        this.messageService?.add({
          severity: 'error',
          summary: 'Error', 
          detail: 'Failed to load entity information'
        });
      }
    });
  }

  /**
   * Handle entity saved event
   */
  onEntitySaved(savedEntity: any, taskForm: UntypedFormGroup): void {
    // Auto-select the newly created entity
    if (savedEntity && savedEntity.id) {
      taskForm.patchValue({ entityId: savedEntity.id });
    }
    
    this.closeEntityDialog();
  }

  /**
   * Handle entity updated event
   */
  onEntityUpdated(updatedEntity: any, taskForm: UntypedFormGroup, markAsTkUpdated: boolean = true): Entity {
    const isTkUpdatedEntity = markAsTkUpdated
      ? 'Yes'
      : updatedEntity?.isTkUpdatedEntity ?? this.currentTaskEntity?.isTkUpdatedEntity;
    this.currentTaskEntity = {
      ...this.currentTaskEntity,
      ...updatedEntity,
      isTkUpdatedEntity
    };
    this.entityToEdit = { ...this.currentTaskEntity };
    
    // If this was a new entity creation, update the task's entityId
    if (updatedEntity && updatedEntity.id) {
      taskForm.patchValue({ entityId: updatedEntity.id });
    }
    
    this.closeUpdateEntityDialog();
    
    return this.currentTaskEntity;
  }

  /**
   * Load entities for dropdown
   */
  loadEntitiesForDropdown(): void {
    this.entitiesService.getEntitiesForDropdown().subscribe({
      next: (response: any) => {
        this.entitiesList = response.data ? response.data.map((entity: any) => ({
          label: entity.entityNames,
          value: entity.id
        })) : [];
      },
      error: (error: any) => {
        console.error('Error loading entities for dropdown:', error);
      }
    });
  }

  /**
   * Clear entity data
   */
  clearEntityData(): void {
    this.currentTaskEntity = null;
    this.entityToEdit = null;
  }

  /**
   * Check if taskReadyToComplete should be enabled
   */
  shouldEnableReadyToComplete(taskForm: UntypedFormGroup): boolean {
    if (this.isEntityRelatedTask(taskForm) && !this.isEntityInfoComplete()) {
      return false;
    }
    return true;
  }

  openEditEntityDialog(): void {
      this.updateEntityDialog = true;
    }
}
